/**
 * Prepare an MOU for a won deal.
 *
 * Opens on the values the system already knows — the lead's identity, its
 * primary contact, and the commercials from the quotation the client actually
 * accepted (falling back to the Closed Won figure). That prefill is one API
 * call rather than four, and it is the reason a signed MOU does not end up
 * quietly disagreeing with the quotation it came from.
 */

import { useEffect, useState } from "react";
import { Select } from "antd";
import { FileSignature, Info, Loader2, X } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { MOU_PREFILL_URL, MOU_TEMPLATES_URL, MOU_URL } from "../../constants/api";

const Field = ({ label, required, hint, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-foreground">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const EMPTY = {
  org_name: "", city: "", state: "",
  title: "Memorandum of Understanding",
  scope_summary: "", terms_text: "",
  contract_value: "", contract_period: "", payment_terms: "",
  effective_date: "", valid_until: "", contract_end_date: "",
  sent_to_name: "", sent_to_email: "", cc_emails: "",
  owner_id: "", quotation_plan_id: null, notes: "",
  template_id: null,
};

export default function PrepareMOUModal({
  customerId, orgName, users = [], onClose, onSuccess,
}) {
  const { fetch, create } = useApiMethods();

  const [form, setForm]       = useState({ ...EMPTY, org_name: orgName || "" });
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(!!customerId);
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});
  const [templates, setTemplates] = useState([]);

  // A lead-less MOU gets no prefill, but still needs the template list.
  useEffect(() => {
    if (customerId) return;
    fetch(MOU_TEMPLATES_URL).then((res) => {
      const list = res?.data?.templates || [];
      setTemplates(list);
      const def = list.find((t) => t.is_default);
      if (def) setForm((prev) => ({ ...prev, template_id: def.id }));
    }).catch(() => {});
  }, [customerId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!customerId) { setLoading(false); return; }
    fetch(MOU_PREFILL_URL(customerId), {}, setLoading).then((res) => {
      if (res?.status !== "success") {
        ToastMsg("error", res?.message || "Could not load the lead");
        return;
      }
      const d = res.data;
      setPrefill(d);
      setForm((prev) => ({
        ...prev,
        org_name:       d.org_name || prev.org_name,
        city:           d.city || "",
        state:          d.state || "",
        title:          d.title || prev.title,
        scope_summary:  d.scope_summary || "",
        terms_text:     d.terms_text || "",
        contract_value: d.contract_value && d.contract_value !== "0" ? d.contract_value : "",
        payment_terms:  d.payment_terms || "",
        effective_date: d.effective_date || "",
        valid_until:    d.valid_until || "",
        sent_to_name:   d.sent_to_name || "",
        sent_to_email:  d.sent_to_email || "",
        cc_emails:      d.cc_emails || "",
        owner_id:       d.owner_id ? String(d.owner_id) : "",
        quotation_plan_id: d.quotation_plan_id || null,
        template_id:    d.default_template_id || null,
      }));
      setTemplates(d.templates || []);
    });
  }, [customerId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const submit = async () => {
    const errs = {};
    if (!(form.org_name || "").trim()) errs.org_name = "Required";
    const value = parseFloat(form.contract_value);
    if (!Number.isFinite(value) || value <= 0) {
      // An MOU with no value commits to nothing, and it is the figure the
      // onboarding record is created from.
      errs.contract_value = "An MOU has to carry a value";
    }
    if (form.sent_to_email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.sent_to_email.trim())) {
      errs.sent_to_email = "Enter a valid email address";
    }
    const badCc = (form.cc_emails || "").split(/[,;]/).map((x) => x.trim()).filter(Boolean)
      .filter((x) => !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(x));
    if (badCc.length) errs.cc_emails = `Not a valid email: ${badCc[0]}`;
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    const res = await create(MOU_URL, { ...form, customer_id: customerId || null });
    setSaving(false);

    if (res?.status === "success") {
      ToastMsg("success", `${res.data?.mou_number || "MOU"} prepared`);
      onSuccess(res.id);
    } else if (res?.mou_id) {
      // Already open for this lead — open it rather than reporting an error at
      // somebody who is trying to do the right thing.
      ToastMsg("info", res.message || "An MOU is already open for this lead — opening it.");
      onSuccess(res.mou_id);
    } else {
      ToastMsg("error", res?.message || "Failed to prepare the MOU");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-background shadow-2xl">

        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
              <FileSignature className="h-4 w-4 text-primary" />
              Prepare MOU
            </h2>
            <p className="text-xs text-muted-foreground">
              {form.org_name || orgName || "New agreement"}
            </p>
          </div>
          <button onClick={onClose}
                  className="text-muted-foreground transition-colors hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading the deal…
            </div>
          ) : (
            <>
              {prefill?.quotation_label && (
                <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Commercials taken from the accepted quotation{" "}
                    <strong>{prefill.quotation_label}</strong>.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Organisation" required>
                  <Input value={form.org_name} onChange={(e) => set("org_name", e.target.value)} />
                  {errors.org_name && <p className="text-[11px] text-red-500">{errors.org_name}</p>}
                </Field>
                <Field label="Title">
                  <Input value={form.title} onChange={(e) => set("title", e.target.value)} />
                </Field>
                <Field label="City">
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
                </Field>
                <Field label="State">
                  <Input value={form.state} onChange={(e) => set("state", e.target.value)} />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Contract value (₹)" required>
                  <Input type="number" value={form.contract_value}
                         onChange={(e) => set("contract_value", e.target.value)} />
                  {errors.contract_value &&
                    <p className="text-[11px] text-red-500">{errors.contract_value}</p>}
                </Field>
                <Field label="Contract period">
                  <Input placeholder="e.g. 3 years" value={form.contract_period}
                         onChange={(e) => set("contract_period", e.target.value)} />
                </Field>
                <Field label="Payment terms">
                  <Input value={form.payment_terms}
                         onChange={(e) => set("payment_terms", e.target.value)} />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Effective from">
                  <Input type="date" value={form.effective_date}
                         onChange={(e) => set("effective_date", e.target.value)} />
                </Field>
                <Field label="Valid until"
                       hint="After this date the client can no longer sign the link.">
                  <Input type="date" value={form.valid_until}
                         onChange={(e) => set("valid_until", e.target.value)} />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Document template"
                       hint="The MOU text is filled in from this — you can edit it before sending.">
                  <Select
                    className="w-full"
                    allowClear
                    placeholder="Built-in standard MOU"
                    value={form.template_id || undefined}
                    onChange={(v) => set("template_id", v || null)}
                    options={templates.map((t) => ({
                      value: t.id, label: t.is_default ? `${t.name} (default)` : t.name,
                    }))}
                  />
                </Field>
                <Field label="Contract end date"
                       hint="When the agreement runs out. Drives renewal alerts.">
                  <Input type="date" value={form.contract_end_date}
                         onChange={(e) => set("contract_end_date", e.target.value)} />
                </Field>
              </div>

              <Field label="Scope"
                     hint="What is being agreed. Shown to the client on the signing page.">
                <Textarea rows={3} value={form.scope_summary}
                          onChange={(e) => set("scope_summary", e.target.value)} />
              </Field>

              <Field label="Terms">
                <Textarea rows={4} value={form.terms_text}
                          onChange={(e) => set("terms_text", e.target.value)} />
              </Field>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Send to">
                  <Input value={form.sent_to_name}
                         onChange={(e) => set("sent_to_name", e.target.value)} />
                </Field>
                <Field label="Email">
                  <Input value={form.sent_to_email}
                         onChange={(e) => set("sent_to_email", e.target.value)} />
                  {errors.sent_to_email &&
                    <p className="text-[11px] text-red-500">{errors.sent_to_email}</p>}
                </Field>
                <Field label="Chased by">
                  <Select
                    className="w-full"
                    placeholder="Owner"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={form.owner_id || undefined}
                    onChange={(v) => set("owner_id", v || "")}
                    options={users}
                  />
                </Field>
              </div>

              <Field label="CC" hint="Copied on the MOU email and the signed copy. Comma separated.">
                <Input value={form.cc_emails} placeholder="accounts@client.com, md@client.com"
                       onChange={(e) => set("cc_emails", e.target.value)} />
                {errors.cc_emails && <p className="text-[11px] text-red-500">{errors.cc_emails}</p>}
              </Field>

              <Field label="Internal notes" hint="Never shown to the client.">
                <Textarea rows={2} value={form.notes}
                          onChange={(e) => set("notes", e.target.value)} />
              </Field>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || loading}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Prepare MOU
          </Button>
        </div>
      </div>
    </div>
  );
}
